import { memo, lazy, Suspense, useCallback } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import Cookies from 'util/cookies';
import Loader from 'components/helpers/Loader';
import { LINKS } from 'configuration';
import { useTranslation } from 'translations';
import PageLayout from 'layouts/PageLayout';

const Pricing = lazy(() => import('pages/Pricing'));
const Login = lazy(() => import('pages/Login'));
const Register = lazy(() => import('pages/Register'));
const ResetPassword = lazy(() => import('pages/ResetPassword'));
const WhatsNew = lazy(() => import('pages/WhatsNew'));
const Store = lazy(() => import('pages/Store'));
const Confirmation = lazy(() => import('pages/Confirmation'));

const LoginRouter = memo(({ requestQuery = '', location }: { requestQuery?: string, location: Location }) => {
  const { tCE } = useTranslation('pages', 'routers');

  const handleRegisterRoute = useCallback(() => {
    if (requestQuery === 'confirm_deletion_user' || requestQuery === 'confirm_registration') {
      Cookies.remove('sessionID');
      return <Navigate replace to={{ pathname: '/', search: location.search }} />;
    }
    if (requestQuery === 'confirm_changed_email' || requestQuery === 'confirm_new_username') {
      return <Navigate replace to={{ pathname: '/', search: location.search }} />;
    }
    if (requestQuery === 'change_password') {
      Cookies.remove('sessionID');
      return <Navigate replace to={{ pathname: LINKS.recoverPassword, search: location.search }} />;
    }
    return <Register />;
  }, [requestQuery, location]);

  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        <Route path={LINKS.login} element={<Login />} />
        <Route path={LINKS.recoverPassword} element={<ResetPassword />} />
        <Route path={LINKS.registration} element={handleRegisterRoute()} />
        <Route path={LINKS.confirm} element={<Confirmation />} />
        <Route path={LINKS.account} element={handleRegisterRoute()} />
        <Route
          path={LINKS.pricing}
          element={
            <PageLayout pageTitle={tCE('menu.subscriptions')}>
              <Pricing />
            </PageLayout>
          }
        />
        <Route path={LINKS.whatsnew} element={<WhatsNew />} />
        <Route
          path={`${LINKS.store}/*`}
          element={
            <PageLayout pageTitle={tCE('menu.store')}>
              <Store />
            </PageLayout>
          }
        />
        <Route path="/" element={<Login />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
});

LoginRouter.displayName = 'LoginRouter';

export default LoginRouter;
